import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  ButtonBaseProps,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import fetch from 'cross-fetch';
import {
  AccountApiResponse,
  ChildProfilePageStep,
  CommonPageStep,
} from '../..';
import { verifyDataFormat } from '../../../common/CommonTools';
import { appConfig } from '../../../common/ConfigLoader';
import { useAppSelector } from '../../../store/hooks';
import {
  AppStateAuthentication,
  selectAppAuthentication,
  setAppPageStep,
  updateAppAuthentication,
  updateAppUserProfile,
} from '../../../store/reducer/appStateSlice';
import {
  selectChildProfileData,
  setChildProfileData,
} from '../../../store/reducer/childProfileDataSlice';
import { outsideComponentDispatch } from '../../../store/store';

export interface CreateChildProfileElementInterface {
  avatarSelector: JSX.Element;
  firstNameInput: JSX.Element;
  lastNameInput: JSX.Element;
  schoolInput: JSX.Element;
  genderSelect: JSX.Element;
  dobDaySelect: JSX.Element;
  dobMonthSelect: JSX.Element;
  dobYearSelect: JSX.Element;
  nextButton: JSX.Element;
}

export const CreateChildProfileElement =
  (): CreateChildProfileElementInterface => {
    // Redux
    const childProfileData = useAppSelector(selectChildProfileData);
    const authenticationData = useAppSelector(selectAppAuthentication);

    // Hooks
    const [nextPressed, setNextPressed] = React.useState(false);

    // Handlers
    return {
      avatarSelector: (
        <React.Fragment>
          <Box
            style={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
          >
            <img
              alt=""
              src={`assets/childProfile/${childProfileData.avatar}.svg`}
              style={{
                position: 'absolute',
                left: '0px',
                width: '200px',
                height: '200px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            />
            <img
              alt=""
              src={`assets/childProfile/avatar-edit-icon-overlay.svg`}
              style={{
                position: 'absolute',
                left: '0px',
                width: '200px',
                height: '200px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              onClick={() => {
                outsideComponentDispatch(
                  setAppPageStep(ChildProfilePageStep.AVATAR),
                );
              }}
            />
            <img
              alt=""
              src={`assets/childProfile/avatar-edit-icon.svg`}
              style={{
                position: 'absolute',
                left: '85px',
                bottom: '15px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            />
          </Box>
        </React.Fragment>
      ),
      firstNameInput: (
        <TextField
          sx={{ width: 161 }}
          color="primary"
          variant="outlined"
          type="text"
          label="First Name"
          size="medium"
          margin="none"
          value={childProfileData.firstname}
          onChange={(event) => {
            outsideComponentDispatch(
              setChildProfileData({
                dataKey: 'firstname',
                data: event.target.value,
              }),
            );
          }}
        />
      ),
      lastNameInput: (
        <TextField
          sx={{ width: 161 }}
          color="primary"
          variant="outlined"
          type="text"
          label="Last Name"
          size="medium"
          margin="none"
          value={childProfileData.lastname}
          onChange={(event) => {
            outsideComponentDispatch(
              setChildProfileData({
                dataKey: 'lastname',
                data: event.target.value,
              }),
            );
          }}
        />
      ),
      schoolInput: (
        <TextField
          fullWidth
          color="primary"
          variant="outlined"
          type="text"
          label="School"
          size="medium"
          margin="none"
          value={childProfileData.school}
          onChange={(event) => {
            outsideComponentDispatch(
              setChildProfileData({
                dataKey: 'school',
                data: event.target.value,
              }),
            );
          }}
        />
      ),
      genderSelect: (
        <FormControl variant="outlined" fullWidth={true}>
          <InputLabel color="primary">Gender</InputLabel>
          <Select
            color="primary"
            size="medium"
            label="Gender"
            onChange={(event) => {
              outsideComponentDispatch(
                setChildProfileData({
                  dataKey: 'gender',
                  data: event.target.value,
                }),
              );
            }}
            value={childProfileData.gender}
          >
            <MenuItem value="MALE">MALE</MenuItem>
            <MenuItem value="FEMALE">FEMALE</MenuItem>
          </Select>
          <FormHelperText />
        </FormControl>
      ),
      dobDaySelect: (
        <FormControl sx={{ width: 106 }} variant="outlined">
          <InputLabel color="primary">Day</InputLabel>
          <Select
            color="primary"
            size="medium"
            label="Day"
            value={childProfileData.dobDay}
            onChange={(event) => {
              outsideComponentDispatch(
                setChildProfileData({
                  dataKey: 'dobDay',
                  data: event.target.value,
                }),
              );
            }}
          >
            <MenuItem value="01">1</MenuItem>
            <MenuItem value="02">2</MenuItem>
            <MenuItem value="03">3</MenuItem>
            <MenuItem value="04">4</MenuItem>
            <MenuItem value="05">5</MenuItem>
            <MenuItem value="06">6</MenuItem>
            <MenuItem value="07">7</MenuItem>
            <MenuItem value="08">8</MenuItem>
            <MenuItem value="09">9</MenuItem>
            <MenuItem value="10">10</MenuItem>
            <MenuItem value="11">11</MenuItem>
            <MenuItem value="12">12</MenuItem>
            <MenuItem value="13">13</MenuItem>
            <MenuItem value="14">14</MenuItem>
            <MenuItem value="15">15</MenuItem>
            <MenuItem value="16">16</MenuItem>
            <MenuItem value="17">17</MenuItem>
            <MenuItem value="18">18</MenuItem>
            <MenuItem value="19">19</MenuItem>
            <MenuItem value="20">20</MenuItem>
            <MenuItem value="21">21</MenuItem>
            <MenuItem value="22">22</MenuItem>
            <MenuItem value="23">23</MenuItem>
            <MenuItem value="24">24</MenuItem>
            <MenuItem value="25">25</MenuItem>
            <MenuItem value="26">26</MenuItem>
            <MenuItem value="27">27</MenuItem>
            <MenuItem value="28">28</MenuItem>
            {childProfileData.dobMonth === '02' ? (
              Number(childProfileData.dobYear) % 4 === 0 ? (
                <MenuItem value="29">29</MenuItem>
              ) : null
            ) : (
              <MenuItem value="29">29</MenuItem>
            )}
            {childProfileData.dobMonth === '02' ? null : (
              <MenuItem value="30">30</MenuItem>
            )}
            {['01', '03', '05', '07', '08', '10', '12'].includes(
              childProfileData.dobMonth,
            ) || !childProfileData.dobMonth ? (
              <MenuItem value="31">31</MenuItem>
            ) : null}
          </Select>
          <FormHelperText />
        </FormControl>
      ),
      dobMonthSelect: (
        <FormControl sx={{ width: 106 }} variant="outlined">
          <InputLabel color="primary">Month</InputLabel>
          <Select
            color="primary"
            size="medium"
            label="Month"
            value={childProfileData.dobMonth}
            onChange={(event) => {
              outsideComponentDispatch(
                setChildProfileData({
                  dataKey: 'dobMonth',
                  data: event.target.value,
                }),
              );
            }}
          >
            <MenuItem value="01">JAN</MenuItem>
            <MenuItem value="02">FEB</MenuItem>
            <MenuItem value="03">MAR</MenuItem>
            <MenuItem value="04">APR</MenuItem>
            <MenuItem value="05">MAY</MenuItem>
            <MenuItem value="06">JUN</MenuItem>
            <MenuItem value="07">JUL</MenuItem>
            <MenuItem value="08">AUG</MenuItem>
            <MenuItem value="09">SEP</MenuItem>
            <MenuItem value="10">OCT</MenuItem>
            <MenuItem value="11">NOV</MenuItem>
            <MenuItem value="12">DEC</MenuItem>
          </Select>
          <FormHelperText />
        </FormControl>
      ),
      dobYearSelect: (
        <FormControl sx={{ width: 106 }} variant="outlined">
          <InputLabel color="primary">Year</InputLabel>
          <Select
            color="primary"
            size="medium"
            label="Year"
            value={childProfileData.dobYear}
            onChange={(event) => {
              outsideComponentDispatch(
                setChildProfileData({
                  dataKey: 'dobYear',
                  data: event.target.value,
                }),
              );
            }}
          >
            <MenuItem value="2010">2010</MenuItem>
            <MenuItem value="2011">2011</MenuItem>
            <MenuItem value="2012">2012</MenuItem>
            <MenuItem value="2013">2013</MenuItem>
            <MenuItem value="2014">2014</MenuItem>
            <MenuItem value="2015">2015</MenuItem>
            <MenuItem value="2016">2016</MenuItem>
            <MenuItem value="2017">2017</MenuItem>
            <MenuItem value="2018">2018</MenuItem>
            <MenuItem value="2019">2019</MenuItem>
            <MenuItem value="2020">2020</MenuItem>
            <MenuItem value="2021">2021</MenuItem>
            <MenuItem value="2022">2022</MenuItem>
          </Select>
          <FormHelperText />
        </FormControl>
      ),
      nextButton: (
        <Button
          variant="contained"
          fullWidth={true}
          disabled={(() => {
            if (nextPressed) {
              return true;
            }

            if (
              !verifyDataFormat(childProfileData.firstname, { type: 'NAME' })
            ) {
              return true;
            }

            if (
              !verifyDataFormat(childProfileData.lastname, { type: 'NAME' })
            ) {
              return true;
            }

            if (
              !verifyDataFormat(childProfileData.gender, { type: 'GENDER' })
            ) {
              return true;
            }

            const birthday =
              childProfileData.dobYear +
              '-' +
              childProfileData.dobMonth +
              '-' +
              childProfileData.dobDay;
            if (!verifyDataFormat(birthday, { type: 'BIRTHDAY' })) {
              return true;
            }

            return false;
          })()}
          onClick={() => {
            // DISABLED THE BUTTON FOR A WHILE
            setNextPressed(true);
            setTimeout(() => {
              setNextPressed(false);
            }, 5000);

            // ADD CHILD PROFILE DATA
            // USER ADD CHILD PROFILE
            const url = `${appConfig().accountServer}user/UserAddChildProfile`;
            const jsonData = {
              firstName: childProfileData.firstname,
              lastName: childProfileData.lastname,
              gender: childProfileData.gender,
              birthday:
                childProfileData.dobYear +
                '-' +
                childProfileData.dobMonth +
                '-' +
                childProfileData.dobDay,
              avatar: childProfileData.avatar,
            };
            const headerData = {
              authorization: authenticationData ? authenticationData.token : '',
            };

            fetch(url, {
              method: 'POST',
              headers: {
                ...headerData,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(jsonData),
            })
              .then(async (res) => {
                const result = (await res.json()) as AccountApiResponse;

                if (!result.success) {
                  throw new Error('ADD CHILD PROFILE ERROR');
                }

                // RELOAD USER PROFILE
                const url = `${appConfig().accountServer}user/UserProfile`;
                const jsonData = {};
                const headerData = {
                  authorization: authenticationData
                    ? authenticationData.token
                    : '',
                };
                await fetch(url, {
                  method: 'POST',
                  headers: {
                    ...headerData,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(jsonData),
                })
                  .then(async (res): Promise<void> => {
                    const result = (await res.json()) as AccountApiResponse;
                    console.log(result);
                    if (result.success) {
                      outsideComponentDispatch(
                        updateAppUserProfile(result.data),
                      );
                    }
                  })
                  .catch((err) => {
                    console.debug('API ERROR', url, err);
                  });

                // NAVIAGATE TO CREATED
                outsideComponentDispatch(
                  setAppPageStep(ChildProfilePageStep.CREATED),
                );
              })
              .catch((err) => {
                console.debug('API ERROR', url, err);
              });
          }}
        >
          Next
        </Button>
      ),
    };
  };
